/** Today checklist reorder — timed blast radius + flex orderKey. Shared mobile/desktop. */
import { format } from 'date-fns';
import {
  buildTodayDisplayOrder,
  reorderByActiveOver,
  getDayAnchorTime,
  computeFlexFlexUpdates,
  computeTimedToFlexUpdates,
  resolveTimedReorderUpdates,
} from '@/features/today/utils/todayListReorderCompute';

export {
  timeToMinutes,
  minutesToTime,
  taskDurationMinutes,
  endMinutesForTask,
} from '@/features/today/utils/todayListReorderTime';
export {
  buildTodayDisplayOrder,
  reorderByActiveOver,
  getDayAnchorTime,
  repackTimedBlast,
} from '@/features/today/utils/todayListReorderCompute';

/**
 * @param {object} params
 * @param {import('@/types').Task[]} params.todayTasks
 * @param {string} params.activeId
 * @param {string} params.overId
 * @returns {Array<{ id: string, time?: string | null, orderKey?: string }>}
 */
export const computeTodayListReorderUpdates = ({
  todayTasks,
  activeId,
  overId,
}) => {
  const displayOrder = buildTodayDisplayOrder(todayTasks);
  const reordered = reorderByActiveOver(displayOrder, activeId, overId);
  if (!reordered) return [];

  const { order: newOrder } = reordered;
  const active = todayTasks.find((task) => task.id === activeId);
  const over = todayTasks.find((task) => task.id === overId);
  if (!active || !over) return [];

  const originalTimed = displayOrder.filter((task) => task.time);
  const originalFlex = displayOrder.filter((task) => !task.time);
  const dayAnchor = getDayAnchorTime(originalTimed);
  const activeWasTimed = Boolean(active.time);
  const overWasTimed = Boolean(over.time);

  if (!activeWasTimed && !overWasTimed) {
    return computeFlexFlexUpdates(originalFlex, activeId, overId);
  }

  if (activeWasTimed && !overWasTimed) {
    return computeTimedToFlexUpdates(newOrder, activeId, active);
  }

  return resolveTimedReorderUpdates({
    activeWasTimed,
    originalTimed,
    originalFlex,
    newOrder,
    activeId,
    overId,
    dayAnchor,
  });
};

/**
 * @param {import('@/types').Task[]} todayTasks
 * @param {string} activeId
 * @param {'up'|'down'} direction
 */
export const computeTodayKeyboardReorderUpdates = (
  todayTasks,
  activeId,
  direction,
) => {
  const displayOrder = buildTodayDisplayOrder(todayTasks);
  const activeIdx = displayOrder.findIndex((task) => task.id === activeId);
  if (activeIdx === -1) return [];

  const overIdx = direction === 'down' ? activeIdx + 1 : activeIdx - 1;
  if (overIdx < 0 || overIdx >= displayOrder.length) return [];

  return computeTodayListReorderUpdates({
    todayTasks,
    activeId,
    overId: displayOrder[overIdx].id,
  });
};

/**
 * Move a task (e.g. from Herplannen) onto today's list with the same timed blast-radius
 * logic as in-list reorder. Without overId, appends after the last visible today task.
 *
 * @param {object} params
 * @param {import('@/types').Task[]} params.todayTasks
 * @param {import('@/types').Task} params.activeTask
 * @param {string} [params.overId]
 * @returns {Array<{ id: string, date?: string, time?: string | null, orderKey?: string }>}
 */
export const computeMoveToTodayUpdates = ({
  todayTasks,
  activeTask,
  overId,
}) => {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const activeId = activeTask.id;
  const withoutActive = todayTasks.filter((task) => task.id !== activeId);
  const mergedTodayTasks = [
    ...withoutActive,
    { ...activeTask, date: todayStr },
  ];

  let targetOverId = overId;
  if (!targetOverId) {
    const order = buildTodayDisplayOrder(mergedTodayTasks);
    const others = order.filter((task) => task.id !== activeId);
    targetOverId = others[others.length - 1]?.id;
  }

  if (!targetOverId) {
    return [{ id: activeId, date: todayStr }];
  }

  const reorderUpdates = computeTodayListReorderUpdates({
    todayTasks: mergedTodayTasks,
    activeId,
    overId: targetOverId,
  });

  if (reorderUpdates.length === 0) {
    return [{ id: activeId, date: todayStr }];
  }

  const hasActive = reorderUpdates.some((update) => update.id === activeId);
  if (!hasActive) {
    return [...reorderUpdates, { id: activeId, date: todayStr }];
  }

  return reorderUpdates.map((update) =>
    update.id === activeId ? { ...update, date: todayStr } : update,
  );
};
